import type { RouteRecord } from 'vite-react-ssg';
import { lazy } from 'react';
import Root from '@/Root';
import { SiteLayout } from '@/layouts/SiteLayout';
import { AppLayout } from '@/layouts/AppLayout';
import { AdminGuard } from '@/components/RequireRole';
import { RouteErrorElement } from '@/components/ErrorBoundary';

// Duas instâncias distintas de propósito: o vite-react-ssg exige `Component` diferente
// por rota renderizada (mesma razão do buildSiteRoutes abaixo).
const NotFoundApp = lazy(() => import('@/app/NotFound'));
const NotFoundGlobal = lazy(() => import('@/app/NotFound'));

// Auth
const Login = lazy(() => import('@/auth/Login'));
const RecuperarSenha = lazy(() => import('@/auth/RecuperarSenha'));

// App
const AppHome = lazy(() => import('@/app/AppHome'));
const Perfil = lazy(() => import('@/app/Perfil'));
const Notificacoes = lazy(() => import('@/app/Notificacoes'));
const MedicoHome = lazy(() => import('@/app/medico/MedicoHome'));
const CirurgiaSala = lazy(() => import('@/app/medico/CirurgiaSala'));

// Clínico
const Pacientes = lazy(() => import('@/app/clinico/Pacientes'));
const PacienteDetalhe = lazy(() => import('@/app/clinico/PacienteDetalhe'));
const AtendimentoDetalhe = lazy(() => import('@/app/clinico/AtendimentoDetalhe'));
const Biblioteca = lazy(() => import('@/app/clinico/Biblioteca'));
const MidiaDetalhe = lazy(() => import('@/app/clinico/MidiaDetalhe'));
const Laudos = lazy(() => import('@/app/clinico/Laudos'));
const Agenda = lazy(() => import('@/app/clinico/Agenda'));
const Gravacoes = lazy(() => import('@/app/clinico/Gravacoes'));
const LaudoDetalhe = lazy(() => import('@/app/clinico/LaudoDetalhe'));

// Admin
const AdminHome = lazy(() => import('@/app/admin/AdminHome'));
const AdminUsuarios = lazy(() => import('@/app/admin/AdminUsuarios'));
const AdminHospitais = lazy(() => import('@/app/admin/AdminHospitais'));
const AdminDispositivos = lazy(() => import('@/app/admin/AdminDispositivos'));
const AdminCirurgias = lazy(() => import('@/app/admin/AdminCirurgias'));
const AdminRelatorios = lazy(() => import('@/app/admin/AdminRelatorios'));
const AdminStorage = lazy(() => import('@/app/admin/AdminStorage'));
const AdminAuditoria = lazy(() => import('@/app/admin/AdminAuditoria'));
const AdminParceiros = lazy(() => import('@/app/admin/AdminParceiros'));

/**
 * Cria rotas do site institucional para um dado prefixo de idioma.
 * IMPORTANTE: cada chamada gera instâncias novas de lazy() porque o
 * vite-react-ssg exige `Component` distinto por rota renderizada.
 */
function buildSiteRoutes(): RouteRecord[] {
  return [
    {
      index: true,
      Component: lazy(() => import('@/site/Home')),
      entry: 'src/site/Home.tsx',
    },
    {
      path: 'plataforma',
      Component: lazy(() => import('@/site/Plataforma')),
      entry: 'src/site/Plataforma.tsx',
    },
    {
      path: 'cases',
      Component: lazy(() => import('@/site/Cases')),
      entry: 'src/site/Cases.tsx',
    },
    {
      path: 'ciencia',
      Component: lazy(() => import('@/site/Ciencia')),
      entry: 'src/site/Ciencia.tsx',
    },
    {
      path: 'ecossistema',
      Component: lazy(() => import('@/site/Ecossistema')),
      entry: 'src/site/Ecossistema.tsx',
    },
    {
      path: 'futuro',
      Component: lazy(() => import('@/site/Futuro')),
      entry: 'src/site/Futuro.tsx',
    },
    {
      path: 'contato',
      Component: lazy(() => import('@/site/Contato')),
      entry: 'src/site/Contato.tsx',
    },
  ];
}

export const routes: RouteRecord[] = [
  {
    path: '/',
    Component: Root,
    entry: 'src/Root.tsx',
    // Sem isto, qualquer throw de render derruba a árvore inteira e vira tela branca.
    ErrorBoundary: RouteErrorElement,
    children: [
      // ===== SITE pt-BR (default sem prefixo) =====
      {
        path: '',
        Component: SiteLayout,
        entry: 'src/layouts/SiteLayout.tsx',
        children: buildSiteRoutes(),
      },
      // ===== SITE en-US (/en) =====
      {
        path: 'en',
        Component: SiteLayout,
        entry: 'src/layouts/SiteLayout.tsx',
        children: buildSiteRoutes(),
      },
      // ===== SITE es (/es) =====
      {
        path: 'es',
        Component: SiteLayout,
        entry: 'src/layouts/SiteLayout.tsx',
        children: buildSiteRoutes(),
      },

      // ===== AUTH (sem prefixo — nesta rodada) =====
      { path: 'login', Component: Login, entry: 'src/auth/Login.tsx' },
      { path: 'recuperar-senha', Component: RecuperarSenha, entry: 'src/auth/RecuperarSenha.tsx' },

      // ===== APP (sem prefixo) =====
      {
        path: 'app',
        Component: AppLayout,
        entry: 'src/layouts/AppLayout.tsx',
        // Boundary próprio: um erro numa tela do app mantém o resto navegável.
        ErrorBoundary: RouteErrorElement,
        children: [
          { index: true, Component: AppHome, entry: 'src/app/AppHome.tsx' },
          { path: 'perfil', Component: Perfil, entry: 'src/app/Perfil.tsx' },
          { path: 'notificacoes', Component: Notificacoes, entry: 'src/app/Notificacoes.tsx' },

          { path: 'medico', Component: MedicoHome, entry: 'src/app/medico/MedicoHome.tsx' },
          { path: 'medico/cirurgia/:id', Component: CirurgiaSala, entry: 'src/app/medico/CirurgiaSala.tsx' },

          { path: 'pacientes', Component: Pacientes, entry: 'src/app/clinico/Pacientes.tsx' },
          { path: 'pacientes/:id', Component: PacienteDetalhe, entry: 'src/app/clinico/PacienteDetalhe.tsx' },
          { path: 'atendimentos/:id', Component: AtendimentoDetalhe, entry: 'src/app/clinico/AtendimentoDetalhe.tsx' },
          { path: 'midia', Component: Biblioteca, entry: 'src/app/clinico/Biblioteca.tsx' },
          { path: 'midia/:id', Component: MidiaDetalhe, entry: 'src/app/clinico/MidiaDetalhe.tsx' },
          { path: 'laudos', Component: Laudos, entry: 'src/app/clinico/Laudos.tsx' },
          { path: 'agenda', Component: Agenda, entry: 'src/app/clinico/Agenda.tsx' },
          { path: 'gravacoes', Component: Gravacoes, entry: 'src/app/clinico/Gravacoes.tsx' },
          { path: 'laudos/:id', Component: LaudoDetalhe, entry: 'src/app/clinico/LaudoDetalhe.tsx' },

          // Rota-layout: o guard de papel roda uma vez e cobre as 8 telas.
          // Registrar cada uma solta deixaria qualquer usuário autenticado entrar
          // digitando a URL — só o menu as escondia.
          {
            path: 'admin',
            Component: AdminGuard,
            entry: 'src/components/RequireRole.tsx',
            children: [
              { index: true, Component: AdminHome, entry: 'src/app/admin/AdminHome.tsx' },
              { path: 'usuarios', Component: AdminUsuarios, entry: 'src/app/admin/AdminUsuarios.tsx' },
              { path: 'hospitais', Component: AdminHospitais, entry: 'src/app/admin/AdminHospitais.tsx' },
              { path: 'parceiros', Component: AdminParceiros, entry: 'src/app/admin/AdminParceiros.tsx' },
              { path: 'dispositivos', Component: AdminDispositivos, entry: 'src/app/admin/AdminDispositivos.tsx' },
              { path: 'cirurgias', Component: AdminCirurgias, entry: 'src/app/admin/AdminCirurgias.tsx' },
              { path: 'relatorios', Component: AdminRelatorios, entry: 'src/app/admin/AdminRelatorios.tsx' },
              { path: 'storage', Component: AdminStorage, entry: 'src/app/admin/AdminStorage.tsx' },
              { path: 'auditoria', Component: AdminAuditoria, entry: 'src/app/admin/AdminAuditoria.tsx' },
            ],
          },

          // 404 dentro de /app: mantém o layout (sidebar) em vez de jogar o usuário fora.
          { path: '*', Component: NotFoundApp, entry: 'src/app/NotFound.tsx' },
        ],
      },

      // 404 global. Fica por último para não sombrear nenhuma rota real.
      { path: '*', Component: NotFoundGlobal, entry: 'src/app/NotFound.tsx' },
    ],
  },
];
